/**
 * SystemSettingsPanel ("Settings")
 *
 * Full-page editor for every kiosk-facing setting of one system, resolved
 * through the level chain. Each row shows the value, the scope an edit applies
 * to, and where the current value comes from. Edits stage locally; saving asks
 * for confirmation listing every change with its blast radius, then writes each
 * change to the level that owns it (station, location, org, or tracking
 * config). Editing is super admin only; everyone else gets the read-only view
 * with a notice.
 */

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Box, Typography } from '@mui/material';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';

import {
    InfoCard,
    EntityPageLayout,
    EntityPageHeader,
    ConfirmDialog,
    SelectField,
    TextField,
    DateInput,
    StatusBadge,
    colors,
    radii,
    typography as typographyTokens,
} from '@components/design-system';
import {
    useEffectiveConfigQuery,
    useCompanyQuery,
    useResellerQuery,
    useLocationQuery,
    useTrackingConfigQuery,
    useWalletBrandsQuery,
    useCompanyMutation,
    useResellerMutation,
    useLocationMutation,
    usePutTrackingConfigMutation,
} from '@features/redux/apis/org';
import { useSystemByIdQuery, useSystemMutation } from '@features/redux/apis/system';
import { useAlert } from '@hooks/useAlert';
import { TrackingFeature, TrackingScopeType } from '@api/org';
import { OrgChainBreadcrumb } from '../../OrgChainBreadcrumb';
import { useOrgDetailLink } from '../../useOrgDetailLink';
import { Selection } from '../../orgNavTypes';
import {
    SettingKey,
    SettingScope,
    SettingEditor,
    EDITABLE_SETTINGS,
    READ_ONLY_NOTES,
    BLADE_CHANGE_NOTE,
    TRACKING_SUB_SETTINGS,
    SETTING_SECTIONS,
    formatSettingValue,
    cadenceValid,
    startDateValid,
} from './settingsMeta';

import type { ManageProps } from '../Panel';
import type { RootState } from '@features/redux/store';
import type { EffectiveSetting } from '@api/org';
import type { SelectOption } from '@components/design-system';
import type { EditableSettingConfig } from './settingsMeta';

type StagedValue = boolean | number | string;

type StagedChange = {
    key: SettingKey;
    label: string;
    config: EditableSettingConfig;
    before: string;
    after: string;
    value: StagedValue;
};

const ON_OFF_OPTIONS: SelectOption[] = [
    { value: 1, label: 'On' },
    { value: 0, label: 'Off' },
];

/** Same-value check across the boolean/number/string forms staging can produce. */
const sameValue = (a: StagedValue | null | undefined, b: StagedValue | null | undefined): boolean =>
    String(a ?? '') === String(b ?? '');

/** Resolved value in the form the editor stages it: dates compare as 'YYYY-MM-DD', not the full instant. */
const comparableValue = (key: SettingKey, value: StagedValue | null | undefined): StagedValue | null | undefined =>
    key === SettingKey.BladeCountingSince && value ? String(value).slice(0, 10) : value;

export const SystemSettingsPanel: React.FC<ManageProps> = ({ id }) => {
    const stationId = Number(id);
    const navigate = useNavigate();
    const detailLink = useOrgDetailLink();
    const role = useSelector((state: RootState) => state.auth.Roles.role);
    const { setAlert } = useAlert();
    const canEdit = role.isSuperAdmin;

    // Effective config is a super-admin endpoint; non-super-admins get the notice below without any fetches
    const { data: config, isLoading, refetch } = useEffectiveConfigQuery({ stationId }, { skip: !stationId || !canEdit });
    const chain = config?.chain;
    const directOrgIsReseller = Boolean(chain?.resellerId);
    const directOrgId = chain?.resellerId ?? chain?.companyId ?? null;
    const directOrgName = chain?.resellerName ?? chain?.companyName ?? null;
    const trackingScopeType = directOrgIsReseller ? TrackingScopeType.Reseller : TrackingScopeType.Company;

    // Full rows for every write target, so a save can echo the record back with only the edited fields changed
    const { data: system } = useSystemByIdQuery({ id: String(stationId) }, { skip: !stationId || !canEdit });
    const { data: company } = useCompanyQuery(
        { companyId: directOrgId! },
        { skip: !directOrgId || directOrgIsReseller },
    );
    const { data: reseller } = useResellerQuery(
        { resellerId: directOrgId! },
        { skip: !directOrgId || !directOrgIsReseller },
    );
    const { data: location } = useLocationQuery(
        { locationId: chain?.locationId ?? 0 },
        { skip: !chain?.locationId },
    );
    const { data: trackingRows } = useTrackingConfigQuery(
        { scopeType: trackingScopeType, scopeId: directOrgId! },
        { skip: !directOrgId },
    );
    const { data: walletBrands } = useWalletBrandsQuery({}, { skip: !canEdit });

    const [mutateSystem] = useSystemMutation();
    const [mutateCompany] = useCompanyMutation();
    const [mutateReseller] = useResellerMutation();
    const [mutateLocation] = useLocationMutation();
    const [putTrackingConfig] = usePutTrackingConfigMutation();

    const [staged, setStaged] = useState<Partial<Record<SettingKey, StagedValue>>>({});
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const settingByKey = new Map((config?.settings ?? []).map((setting) => [setting.key as SettingKey, setting]));

    const walletOptions: SelectOption[] = [
        { value: '', label: 'None' },
        ...Object.entries(walletBrands ?? {}).map(([slug, brandName]) => ({ value: slug, label: brandName })),
    ];

    const stageChange = (key: SettingKey, value: StagedValue) =>
        setStaged((previous) => {
            const next = { ...previous };
            if (sameValue(value, comparableValue(key, settingByKey.get(key)?.value as StagedValue | null))) {
                delete next[key];
            } else {
                next[key] = value;
            }
            // Staging a tracking flag off orphans its sub-settings; drop them with it
            if (value === false) {
                (TRACKING_SUB_SETTINGS[key] ?? []).forEach((subKey) => delete next[subKey]);
            }
            return next;
        });

    const formatValue = (key: SettingKey, value: StagedValue | null): string => {
        if (key === SettingKey.WalletTemplate && value && walletBrands?.[String(value)]) {
            return walletBrands[String(value)];
        }
        return formatSettingValue(key, value);
    };

    const changes: StagedChange[] = Object.entries(staged).flatMap(([key, value]) => {
        const settingKey = key as SettingKey;
        const editableConfig = EDITABLE_SETTINGS[settingKey];
        const setting = settingByKey.get(settingKey);
        if (!editableConfig || !setting || value === undefined) return [];
        return [
            {
                key: settingKey,
                label: setting.label,
                config: editableConfig,
                before: formatValue(settingKey, setting.value as StagedValue | null),
                after: formatValue(settingKey, value),
                value,
            },
        ];
    });

    const isDirty = changes.length > 0;
    const isValid = changes.every(({ config: editableConfig, value }) => {
        if (editableConfig.editor === SettingEditor.Cadence) return cadenceValid(String(value));
        if (editableConfig.editor === SettingEditor.Date) return startDateValid(String(value));
        return true;
    });

    /** Blast-radius groups for the confirm dialog, most specific first. */
    const changeGroups = [
        {
            heading: `This kiosk only (${chain?.serialNum ?? `#${stationId}`})`,
            changes: changes.filter(({ config: c }) => c.scope === SettingScope.Station),
        },
        {
            heading: `All kiosks at ${chain?.locationName ?? 'this store'}`,
            changes: changes.filter(({ config: c }) => c.scope === SettingScope.Location),
        },
        {
            heading: `All kiosks under ${directOrgName ?? 'this organization'}`,
            changes: changes.filter(({ config: c }) => c.scope === SettingScope.Org || c.scope === SettingScope.Tracking),
        },
    ].filter((group) => group.changes.length > 0);

    const saveTracking = (trackingChanges: StagedChange[]) => {
        const features = [...new Set(trackingChanges.map(({ config: c }) => c.trackingFeature!))];

        return features.map((feature) => {
            const row = (trackingRows ?? []).find((trackingRow) => trackingRow.Feature === feature);
            const forFeature = (field: 'Enabled' | 'CadenceCuts' | 'StartDate') =>
                trackingChanges.find(
                    ({ config: c }) => c.trackingFeature === feature && c.trackingField === field,
                );

            const enabledChange = forFeature('Enabled');
            const cadenceChange = forFeature('CadenceCuts');
            const startDateChange = forFeature('StartDate');

            const resolvedEnabled = Boolean(
                settingByKey.get(feature === TrackingFeature.Blade ? SettingKey.BladeTracking : SettingKey.KitTracking)
                    ?.value,
            );
            const enabled = enabledChange ? Boolean(enabledChange.value) : row?.Enabled ?? resolvedEnabled;
            const cadence = cadenceChange ? Number(cadenceChange.value) : row?.CadenceCuts ?? null;
            // StartDate is required while enabled; keep the existing date, defaulting to today for a fresh enable
            const startDate = startDateChange
                ? String(startDateChange.value)
                : row?.StartDate
                ? row.StartDate.slice(0, 10)
                : dayjs().format('YYYY-MM-DD');

            return putTrackingConfig({
                config: {
                    Feature: feature,
                    ScopeType: trackingScopeType,
                    ScopeID: directOrgId!,
                    Enabled: enabled,
                    CadenceCuts: enabled && cadence ? cadence : null,
                    StartDate: enabled ? startDate : null,
                },
            });
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const byScope = (scope: SettingScope) => changes.filter(({ config: c }) => c.scope === scope);
            const pending: Promise<unknown>[] = [];

            const stationChanges = byScope(SettingScope.Station);
            if (stationChanges.length && system) {
                const body = { ...system };
                stationChanges.forEach(({ config: c, value }) => {
                    (body as Record<string, unknown>)[c.stationField!] =
                        c.editor === SettingEditor.Toggle ? (value ? 1 : 0) : value;
                });
                pending.push(mutateSystem({ system: body }));
            }

            const orgChanges = byScope(SettingScope.Org);
            const orgRecord = directOrgIsReseller ? reseller : company;
            if (orgChanges.length && orgRecord) {
                const body = { ...orgRecord } as Record<string, unknown>;
                orgChanges.forEach(({ config: c, value }) => {
                    body[c.orgField!] = Boolean(value);
                });
                pending.push(
                    directOrgIsReseller
                        ? mutateReseller({ method: 'PATCH', reseller: body })
                        : mutateCompany({ method: 'PATCH', company: body }),
                );
            }

            const walletChange = byScope(SettingScope.Location)[0];
            if (walletChange && location) {
                pending.push(
                    mutateLocation({
                        method: 'PATCH',
                        location: { ...location, WalletTemplate: (walletChange.value as string) || null },
                    }),
                );
            }

            pending.push(...saveTracking(byScope(SettingScope.Tracking)));

            const results = await Promise.all(pending);
            const failed = results.some((result) => result && typeof result === 'object' && 'error' in result);

            if (failed) {
                setAlert('Some kiosk settings failed to save', 'error');
            } else {
                setAlert('Kiosk settings saved', 'success');
                setStaged({});
            }
            refetch();
        } finally {
            setSaving(false);
            setConfirmOpen(false);
        }
    };

    /** Where an edit to this row lands, in the same words the confirm dialog uses. */
    const scopeLabel = (editableConfig: EditableSettingConfig): string => {
        if (editableConfig.scope === SettingScope.Station) return 'This kiosk';
        if (editableConfig.scope === SettingScope.Location) return `This store · ${chain?.locationName ?? ''}`;
        return `All kiosks · ${directOrgName ?? ''}`;
    };

    const sourceLabel = (setting: EffectiveSetting): string => {
        if (setting.source.level === 'Default') return 'Default';
        return `Set at ${setting.source.level}${setting.source.name ? ` · ${setting.source.name}` : ''}`;
    };

    const renderEditor = (setting: EffectiveSetting, editableConfig: EditableSettingConfig) => {
        const key = setting.key as SettingKey;
        const stagedValue = staged[key];

        if (editableConfig.editor === SettingEditor.Cadence) {
            const value = stagedValue !== undefined ? String(stagedValue) : String(setting.value ?? '');
            const invalid = stagedValue !== undefined && !cadenceValid(value);
            return (
                <TextField
                    label=''
                    value={value}
                    error={invalid}
                    errorMessage={invalid ? 'Enter a positive whole number of cuts' : undefined}
                    onChange={(next) => stageChange(key, next)}
                    sx={{ maxWidth: 220 }}
                />
            );
        }

        if (editableConfig.editor === SettingEditor.Date) {
            const value =
                stagedValue !== undefined
                    ? String(stagedValue)
                    : setting.value
                    ? String(setting.value).slice(0, 10)
                    : dayjs().format('YYYY-MM-DD');
            const invalid = stagedValue !== undefined && !startDateValid(value);
            return (
                <Box sx={{ maxWidth: 220 }}>
                    <DateInput
                        label=''
                        value={value}
                        max={dayjs().format('YYYY-MM-DD')}
                        error={invalid}
                        helperText={invalid ? 'Pick a date that is today or earlier' : undefined}
                        fullWidth
                        onChange={(next) => stageChange(key, next)}
                    />
                </Box>
            );
        }

        if (editableConfig.editor === SettingEditor.Toggle) {
            const current = stagedValue !== undefined ? Boolean(stagedValue) : Boolean(setting.value);
            return (
                <SelectField
                    label=''
                    value={current ? 1 : 0}
                    options={ON_OFF_OPTIONS}
                    onChange={(next) => stageChange(key, Boolean(next))}
                    size='small'
                    sx={{ maxWidth: 220 }}
                />
            );
        }

        const options = key === SettingKey.WalletTemplate ? walletOptions : editableConfig.options ?? [];
        const current =
            stagedValue !== undefined
                ? stagedValue
                : key === SettingKey.WalletTemplate
                ? String(setting.value ?? '')
                : (setting.value as number);

        return (
            <SelectField
                label=''
                value={current as string | number}
                options={options}
                onChange={(next) => stageChange(key, next)}
                size='small'
                sx={{ maxWidth: 220 }}
            />
        );
    };

    const renderRow = (setting: EffectiveSetting, indented: boolean, striped: boolean) => {
        const key = setting.key as SettingKey;
        // A logged blade change on this kiosk outranks the org start date, so editing the date would do nothing here
        const stationOverridesDate = key === SettingKey.BladeCountingSince && setting.source.level === 'Station';
        const editableConfig = stationOverridesDate ? undefined : EDITABLE_SETTINGS[key];
        const readOnlyNote = stationOverridesDate ? BLADE_CHANGE_NOTE : READ_ONLY_NOTES[key];
        const edited = staged[key] !== undefined;

        return (
            <Box
                key={key}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: indented ? '164px 1fr auto' : '180px 1fr auto' },
                    alignItems: 'center',
                    columnGap: 1.5,
                    rowGap: 0.5,
                    px: 1.25,
                    py: indented ? 0.625 : 1,
                    pl: { xs: indented ? 2.5 : 1.25, sm: indented ? 3 : 1.25 },
                    borderRadius: `${radii.sm}px`,
                    backgroundColor: edited ? colors.primary[50] : striped ? colors.neutral[50] : 'transparent',
                }}>
                <Typography
                    sx={{
                        fontSize: indented ? typographyTokens.fontSize.xs : typographyTokens.fontSize.sm,
                        fontWeight: indented ? typographyTokens.fontWeight.normal : typographyTokens.fontWeight.medium,
                        color: indented ? colors.neutral[500] : colors.neutral[600],
                    }}>
                    {setting.label}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {canEdit && editableConfig ? (
                        renderEditor(setting, editableConfig)
                    ) : typeof setting.value === 'boolean' ? (
                        <StatusBadge
                            label={setting.value ? 'On' : 'Off'}
                            status={setting.value ? 'success' : 'neutral'}
                            size='small'
                        />
                    ) : (
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.sm,
                                color: setting.value === null ? colors.neutral[400] : colors.neutral[800],
                                fontStyle: setting.value === null ? 'italic' : 'normal',
                            }}>
                            {formatValue(key, setting.value as StagedValue | null)}
                        </Typography>
                    )}
                    {!editableConfig && readOnlyNote && (
                        <Typography sx={{ fontSize: typographyTokens.fontSize.xs, color: colors.neutral[400] }}>
                            {readOnlyNote}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ justifySelf: { xs: 'start', sm: 'end' }, textAlign: { xs: 'left', sm: 'right' } }}>
                    {editableConfig && (
                        <Typography sx={{ fontSize: typographyTokens.fontSize.xs, color: colors.neutral[600] }}>
                            {scopeLabel(editableConfig)}
                        </Typography>
                    )}
                    <Typography sx={{ fontSize: typographyTokens.fontSize.xs, color: colors.neutral[400] }}>
                        {sourceLabel(setting)}
                    </Typography>
                </Box>
            </Box>
        );
    };

    const sections = SETTING_SECTIONS.map((section) => ({
        title: section.title,
        rows: section.rows
            .filter((row) => settingByKey.has(row.key))
            .map((row) => {
                // Sub-settings only apply while the parent flag is on; staged toggles count so they appear immediately
                const parentOn = Boolean(staged[row.key] ?? settingByKey.get(row.key)!.value);
                return {
                    setting: settingByKey.get(row.key)!,
                    subSettings: (parentOn ? row.subRows ?? [] : [])
                        .map((subKey) => settingByKey.get(subKey))
                        .filter((subSetting): subSetting is EffectiveSetting => Boolean(subSetting)),
                };
            }),
    })).filter((section) => section.rows.length > 0);

    const systemCrumb = chain?.serialNum ? `System · ${chain.serialNum}` : `System #${stationId}`;

    if (!canEdit) {
        return (
            <EntityPageLayout loading={false}>
                <OrgChainBreadcrumb
                    ancestors={[{ type: Selection.System, id: stationId, name: systemCrumb }]}
                    current='Settings'
                />

                <EntityPageHeader
                    entityType='system'
                    title='Settings'
                    id={stationId}
                    icon={<TuneOutlinedIcon />}
                    loading={false}
                    onCancel={() => navigate(detailLink(Selection.System, stationId))}
                />

                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: colors.info.light,
                        border: `1px solid ${colors.info.main}`,
                    }}>
                    <Typography variant='body2'>
                        Kiosk settings require super admin access. Ask a super admin if you need a setting changed for
                        this kiosk.
                    </Typography>
                </Box>
            </EntityPageLayout>
        );
    }

    return (
        <EntityPageLayout loading={isLoading && !config}>
            <OrgChainBreadcrumb
                ancestors={[
                    chain?.companyId && chain?.companyName
                        ? { type: Selection.Company, id: chain.companyId, name: chain.companyName }
                        : null,
                    chain?.resellerId && chain?.resellerName
                        ? { type: Selection.Reseller, id: chain.resellerId, name: chain.resellerName }
                        : null,
                    chain?.locationId && chain?.locationName
                        ? { type: Selection.Location, id: chain.locationId, name: chain.locationName }
                        : null,
                    { type: Selection.System, id: stationId, name: systemCrumb },
                ]}
                current='Settings'
            />

            <EntityPageHeader
                entityType='system'
                title='Settings'
                id={stationId}
                subtitle={chain?.serialNum ?? undefined}
                icon={<TuneOutlinedIcon />}
                loading={isLoading && !config}
                onCancel={() => navigate(detailLink(Selection.System, stationId))}
                onSave={canEdit ? () => setConfirmOpen(true) : undefined}
                saveDisabled={!isDirty || !isValid || saving}
                saving={saving}
            />

            <InfoCard
                title='Kiosk Settings'
                icon={<TuneOutlinedIcon />}
                note='Settings apply to this kiosk unless they are set for the whole store or company. Each row shows the scope a change applies to.'>
                {sections.map((section) => (
                    <Box key={section.title} sx={{ '&:not(:first-of-type)': { mt: 2 } }}>
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.sm,
                                fontWeight: typographyTokens.fontWeight.semibold,
                                color: colors.neutral[500],
                                pb: 0.5,
                                mb: 0.5,
                                borderBottom: `1px solid ${colors.neutral[150]}`,
                            }}>
                            {section.title}
                        </Typography>
                        {section.rows.map(({ setting, subSettings }, rowIndex) => (
                            <Box key={setting.key}>
                                {renderRow(setting, false, rowIndex % 2 === 0)}
                                {subSettings.map((subSetting) => renderRow(subSetting, true, rowIndex % 2 === 0))}
                            </Box>
                        ))}
                    </Box>
                ))}
            </InfoCard>

            <ConfirmDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleSave}
                title='Confirm settings changes'
                variant='warning'
                confirmText='Save changes'
                loading={saving}
                message={
                    <Box>
                        {changeGroups.map((group) => (
                            <Box key={group.heading} sx={{ '&:not(:first-of-type)': { mt: 1.5 } }}>
                                <Typography
                                    sx={{
                                        fontWeight: typographyTokens.fontWeight.semibold,
                                        fontSize: typographyTokens.fontSize.sm,
                                    }}>
                                    {group.heading}
                                </Typography>
                                {group.changes.map((change) => (
                                    <Typography
                                        key={change.key}
                                        sx={{ fontSize: typographyTokens.fontSize.sm, pl: 1.5, pt: 0.25 }}>
                                        {change.label}: {change.before} → {change.after}
                                    </Typography>
                                ))}
                            </Box>
                        ))}
                    </Box>
                }
            />
        </EntityPageLayout>
    );
};

export default SystemSettingsPanel;
